import {
  Alert,
  Badge,
  Button,
  Checkbox,
  Group,
  Loader,
  MultiSelect,
  NumberInput,
  Select,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
  Textarea,
  TextInput,
  Tooltip,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconCheck,
  IconExternalLink,
  IconRefresh,
  IconUserPlus,
} from "@tabler/icons-react";
import { Link } from "react-router-dom";
import { CityMultiSelect } from "./CityMultiSelect";
import { CtcLakhsInput } from "./CtcLakhsInput";
import { PhoneInput } from "./PhoneInput";
import { GENDER_OPTIONS } from "../lib/formCatalogs";
import { validateOptionalEmail } from "../lib/email";
import { validateIndianPhoneField } from "../lib/phone";
import { useRoleFamilyCatalog } from "../lib/useRoleFamilyCatalog";
import { useSegmentCatalog } from "../lib/useSegmentCatalog";
import { useSkillCatalog } from "../lib/useSkillCatalog";
import {
  BULK_SAVE_NEEDS_FIELDS,
  isBulkItemSaveable,
  type BulkItem,
  validateBulkGender,
  validateBulkName,
  validateBulkSegments,
} from "../pages/bulkTypes";
import { axioTokens } from "../theme";

interface BulkCandidateCardProps {
  item: BulkItem;
  onPatch: (id: string, patch: Partial<BulkItem>) => void;
  onRetry: (id: string) => void;
  onSaveToPool: (id: string) => Promise<void>;
  saving?: boolean;
}

export function BulkCandidateCard({
  item,
  onPatch,
  onRetry,
  onSaveToPool,
  saving = false,
}: BulkCandidateCardProps) {
  if (item.status === "pending" || item.status === "uploading" || item.status === "parsing") {
    return <SkeletonCard item={item} />;
  }

  if (item.status === "error") {
    return (
      <article className="bulkCard">
        <header className="bulkCardHeader">
          <Text size="sm" fw="var(--axio-font-weight-semibold)">{item.fileName}</Text>
        </header>
        <div className="bulkCardBody">
          <Alert color="red" variant="light">
            {item.errorMessage ?? "Could not process this resume."}
          </Alert>
        </div>
        <div className="bulkCardFooter">
          <Button
            size="xs"
            variant="default"
            leftSection={<IconRefresh size={axioTokens.unit[14]} />}
            onClick={() => onRetry(item.id)}
            disabled={!item.fileRef && !item.resumeId}
          >
            Retry
          </Button>
        </div>
      </article>
    );
  }

  return <DoneCard item={item} onPatch={onPatch} onSaveToPool={onSaveToPool} saving={saving} />;
}

function statusLabel(status: BulkItem["status"]): string {
  if (status === "uploading") return "Uploading…";
  if (status === "parsing") return "Parsing…";
  return "Waiting…";
}

function SkeletonCard({ item }: { item: BulkItem }) {
  return (
    <article className="bulkCard bulkCardSkeleton">
      <header className="bulkCardHeader">
        <Text size="sm" c="dimmed" lineClamp={1}>
          {item.fileName}
        </Text>
        <Group gap="xs" wrap="nowrap">
          <Loader size="xs" color="violet" />
          <Text size="xs" c="dimmed">
            {statusLabel(item.status)}
          </Text>
        </Group>
      </header>
      <div className="bulkCardBody">
        <Skeleton height={axioTokens.unit[14]} width={axioTokens.percentage.fourFifths} />
        <Skeleton height={axioTokens.unit[12]} width={axioTokens.percentage.threeFifths} />
        <Skeleton height={axioTokens.unit[12]} width={axioTokens.percentage.twoFifths} />
      </div>
    </article>
  );
}

function DoneCard({
  item,
  onPatch,
  onSaveToPool,
  saving = false,
}: Pick<BulkCandidateCardProps, "item" | "onPatch" | "onSaveToPool" | "saving">) {
  const skillCatalog = useSkillCatalog();
  const segmentCatalog = useSegmentCatalog();
  const roleFamilyCatalog = useRoleFamilyCatalog();
  const saveable = isBulkItemSaveable(item);
  const locked = item.candidateId !== null;

  return (
    <article className={`bulkCard ${item.candidateId ? "bulkCardSaved" : ""}`}>
      <header className="bulkCardHeader">
        <TextInput
          className="bulkCardNameInput"
          label="Full name"
          required
          value={item.parsedName}
          onChange={(event) => onPatch(item.id, { parsedName: event.currentTarget.value })}
          error={locked ? undefined : (validateBulkName(item.parsedName) ?? undefined)}
          size="sm"
          disabled={locked}
        />
        {saving ? <Loader size="xs" /> : null}
        {item.candidateId ? (
          <Badge color="green" leftSection={<IconCheck size={axioTokens.unit[12]} />}>
            Saved
          </Badge>
        ) : null}
      </header>

      <div className="bulkCardBody">
        <Stack gap="sm">
          {locked ? (
            <Text size="xs" c="dimmed">
              Saved to pool. Open the profile to edit.
            </Text>
          ) : null}
          <SimpleGrid
            type="container"
            cols={{ base: 1, [axioTokens.containerQuery.sm]: 2 }}
            spacing="sm"
          >
            <PhoneInput
              label="Phone"
              required
              value={item.parsedPhone}
              onChange={(value) => onPatch(item.id, { parsedPhone: value })}
              error={locked ? undefined : (validateIndianPhoneField(item.parsedPhone) ?? undefined)}
              size="sm"
              disabled={locked}
            />
            <TextInput
              label="Email"
              value={item.parsedEmail}
              onChange={(event) => onPatch(item.id, { parsedEmail: event.currentTarget.value })}
              error={locked ? undefined : (validateOptionalEmail(item.parsedEmail) ?? undefined)}
              size="sm"
              disabled={locked}
            />
            <TextInput
              label="Current title"
              value={item.parsedTitle}
              onChange={(event) => onPatch(item.id, { parsedTitle: event.currentTarget.value })}
              size="sm"
              disabled={locked}
            />
            <NumberInput
              label="Experience (yrs)"
              value={item.parsedExperience ?? ""}
              onChange={(value) => onPatch(item.id, { parsedExperience: typeof value === "number" ? value : null })}
              min={0}
              max={60}
              step={1}
              allowDecimal={false}
              size="sm"
              disabled={locked}
            />
            <Select
              label="Gender"
              required
              data={[...GENDER_OPTIONS]}
              value={item.parsedGender || null}
              onChange={(value) => onPatch(item.id, { parsedGender: value ?? "" })}
              error={locked ? undefined : (validateBulkGender(item.parsedGender) ?? undefined)}
              size="sm"
              disabled={locked}
            />
            <Select
              label="Role family"
              data={roleFamilyCatalog.options}
              value={item.parsedRoleFamily || null}
              onChange={(value) => onPatch(item.id, { parsedRoleFamily: value ?? "" })}
              searchable
              clearable
              size="sm"
              disabled={locked}
            />
            <CtcLakhsInput
              label="Current CTC"
              value={item.parsedCurrentCtcLakhs ?? ""}
              onChange={(value) => onPatch(item.id, { parsedCurrentCtcLakhs: typeof value === "number" ? value : null })}
              size="sm"
              disabled={locked}
            />
            <CtcLakhsInput
              label="Expected CTC"
              value={item.parsedExpectedCtcLakhs ?? ""}
              onChange={(value) => onPatch(item.id, { parsedExpectedCtcLakhs: typeof value === "number" ? value : null })}
              size="sm"
              disabled={locked}
            />
          </SimpleGrid>
          <MultiSelect
            label="Segments"
            required
            data={segmentCatalog.flatOptions}
            value={item.parsedSegments}
            onChange={(segments) => onPatch(item.id, { parsedSegments: segments })}
            error={locked ? undefined : (validateBulkSegments(item.parsedSegments) ?? undefined)}
            searchable
            clearable
            size="sm"
            disabled={locked}
          />
          <CityMultiSelect
            label="Cities"
            value={item.parsedCities}
            onChange={(cities) => onPatch(item.id, { parsedCities: cities })}
            size="sm"
            disabled={locked}
          />
          <MultiSelect
            label="Skills"
            data={skillCatalog.flatOptions}
            value={item.parsedSkills}
            onChange={(skills) => onPatch(item.id, { parsedSkills: skills })}
            searchable
            clearable
            size="sm"
            disabled={locked}
          />
          <Textarea
            label="Notes about this candidate"
            placeholder="Anything you want to remember"
            value={item.callNotes}
            onChange={(event) => onPatch(item.id, { callNotes: event.currentTarget.value })}
            autosize
            minRows={2}
            maxRows={4}
            size="sm"
            disabled={locked}
          />
        </Stack>
        {item.submitError ? (
          <Alert icon={<IconAlertCircle size={axioTokens.unit[16]} />} color="red" mt="xs">
            {item.submitError}
          </Alert>
        ) : null}
      </div>

      <div className="bulkCardFooter">
        {item.candidateId ? (
          <>
            <Checkbox
              label="Select for job submission"
              checked={item.selectedForSubmit}
              onChange={(event) => onPatch(item.id, { selectedForSubmit: event.currentTarget.checked })}
            />
            <Button
              component={Link}
              to={`/candidates/${item.candidateId}`}
              variant="subtle"
              size="xs"
              rightSection={<IconExternalLink size={axioTokens.unit[14]} />}
            >
              View profile
            </Button>
          </>
        ) : (
          <Tooltip
            label={BULK_SAVE_NEEDS_FIELDS}
            disabled={saveable}
          >
            <span className="bulkCardSaveTooltip">
              <Button
                color="violet"
                size="sm"
                leftSection={<IconUserPlus size={axioTokens.unit[16]} />}
                loading={saving}
                disabled={!saveable || saving}
                onClick={() => void onSaveToPool(item.id)}
              >
                Save to Pool
              </Button>
            </span>
          </Tooltip>
        )}
      </div>
    </article>
  );
}
